import { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { motion } from 'motion/react';
import {
  LayoutDashboard, FolderKanban, Calendar, MapPin, Megaphone, CheckSquare,
  Heart, ShoppingBag, Target, ChevronLeft, ChevronRight, ChevronDown, Plus,
  UsersRound, LogOut, Shield, Plug, Globe, ExternalLink,
  Bell, AtSign, CheckCheck, X, Menu, MapPin as MapPinIcon, Clock as ClockIcon,
  Settings, LayoutTemplate, Mail, Smartphone, Keyboard, Key, BookOpen, Search,
  Building2, MapPinned, History, Briefcase,
} from 'lucide-react';
import { cn } from './ui/utils';
import { StageFlowLogo } from './StageFlowLogo';
import { useIsMobile } from './ui/use-mobile';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';
import { useAuth } from '../data/AuthContext';
import { useData } from '../data/DataContext';
import { fetchInboxCounts, fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../data/api';
import type { MentionNotification } from '../data/api';
import { VoiceCommandButton } from './VoiceCommandButton';
import { FeedbackButton } from './FeedbackButton';
import { KeyboardShortcutsOverlay } from './KeyboardShortcutsOverlay';
import { HelpSearchDialog } from './help/HelpSearchDialog';
import { DailyResetOverlay } from './DailyResetOverlay';
import { PageTransition } from './ui/motion';

const POLL_INTERVAL = 60_000; // 60 seconds

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', description: 'Your command center' },
  { to: '/deliverables', icon: CheckSquare, label: 'Deliverables', description: 'List, kanban, calendar & more' },
  { to: '/calendar', icon: Calendar, label: 'Calendar', description: 'Schedule & events' },
  { to: '/content', icon: Megaphone, label: 'Content', description: 'Social media & posts' },
  { to: '/volunteers', icon: Heart, label: 'Volunteers', description: 'Your amazing helpers' },
  { to: '/merch', icon: ShoppingBag, label: 'Merch', description: 'Products & designs' },
  // Primary nav rather than a setting: the hub is for everyone on the roster,
  // not just admins.
  { to: '/hr', icon: Briefcase, label: 'Employee Hub', description: 'Directory, records & org chart' },
  { to: '/help', icon: BookOpen, label: 'Help & Wiki', description: 'Guides, articles, glossary' },
];

const settingsItems = [
  { to: '/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/venues', icon: MapPin, label: 'Venues' },
  { to: '/team', icon: UsersRound, label: 'Team' },
  { to: '/team/presence', icon: MapPinIcon, label: 'Presence' },
  { to: '/team/hours', icon: ClockIcon, label: 'Hours' },
  { to: '/templates', icon: LayoutTemplate, label: 'Templates' },
  { to: '/integrations', icon: Plug, label: 'Integrations' },
  { to: '/digest', icon: Mail, label: 'Daily Digest' },
  { to: '/settings/departments', icon: Building2, label: 'Departments' },
  { to: '/settings/locations', icon: MapPinned, label: 'Locations' },
  { to: '/settings/audit-log', icon: History, label: 'Audit Log' },
  { to: '/settings/api', icon: Key, label: 'API' },
];

export function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [notifications, setNotifications] = useState<MentionNotification[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  const effectiveCollapsed = isMobile ? false : collapsed;
  const { currentUser, isAdmin, logout } = useAuth();
  const { loading: dataLoading } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const isCommunityActive = location.pathname.startsWith('/inbox') || location.pathname.startsWith('/community');
  const isSettingsActive = ['/projects', '/venues', '/team', '/integrations', '/tv', '/templates', '/digest', '/settings'].some(p => location.pathname.startsWith(p));
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifButtonRef = useRef<HTMLButtonElement>(null);
  const notifDropdownRef = useRef<HTMLDivElement>(null);

  // Auto-expand community section when navigating to any community page
  useEffect(() => {
    if (isCommunityActive) setCommunityOpen(true);
  }, [isCommunityActive]);

  // Auto-expand settings section when navigating to any settings page
  useEffect(() => {
    if (isSettingsActive) setSettingsOpen(true);
  }, [isSettingsActive]);

  // Auto-close mobile drawer on navigation
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // Poll for inbox counts — stagger after initial data load to avoid
  // thundering-herd on cold-start edge function
  const loadInboxCounts = useCallback(async () => {
    try {
      const data = await fetchInboxCounts();
      setInboxCount(data.total);
    } catch {
      // Silently fail — badge just won't update
    }
  }, []);

  useEffect(() => {
    if (dataLoading) return; // Don't poll until initial data is loaded
    // Stagger 2s after data load so the edge function isn't overwhelmed
    const delay = setTimeout(() => {
      loadInboxCounts();
      pollRef.current = setInterval(loadInboxCounts, POLL_INTERVAL);
    }, 2000);
    return () => {
      clearTimeout(delay);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadInboxCounts, dataLoading]);

  // Clear badge count when user is viewing inbox
  // (they're seeing the items, so the badge is redundant)
  const displayCount = location.pathname === '/inbox' ? 0 : inboxCount;

  // Load mention notifications for current user
  const loadNotifications = useCallback(async () => {
    if (!currentUser?.name) return;
    try {
      const data = await fetchNotifications(currentUser.name);
      setNotifications(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch {
      // Silently fail
    }
  }, [currentUser?.name]);

  useEffect(() => {
    if (dataLoading) return; // Don't poll until initial data is loaded
    // Stagger 3s (after inbox counts) so calls don't overlap
    const delay = setTimeout(() => {
      loadNotifications();
      notifPollRef.current = setInterval(loadNotifications, POLL_INTERVAL);
    }, 3000);
    return () => {
      clearTimeout(delay);
      if (notifPollRef.current) clearInterval(notifPollRef.current);
    };
  }, [loadNotifications, dataLoading]);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (notifButtonRef.current?.contains(target)) return;
      if (notifDropdownRef.current?.contains(target)) return;
      setNotifOpen(false);
    };
    if (notifOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const unreadNotifCount = notifications.filter(n => !n.read).length;

  // Mark a notification as read and navigate to deliverable
  const handleNotifClick = useCallback(async (notif: MentionNotification) => {
    if (!notif.read) {
      try {
        await markNotificationRead(notif.id);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      } catch { /* ignore */ }
    }
    setNotifOpen(false);
    // Agenda notifications carry their own link; older notification types
    // always point at a deliverable.
    const target = notif.link || (notif.deliverableId ? `/deliverables/${notif.deliverableId}` : null);
    if (target) navigate(target);
  }, [navigate]);

  // Mark all notifications as read
  const handleMarkAllRead = useCallback(async () => {
    if (!currentUser?.name) return;
    try {
      await markAllNotificationsRead(currentUser.name);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  }, [currentUser?.name]);

  // Relative time helper for notifications
  const relativeTime = (dateStr: string) => {
    const now = new Date();
    const d = new Date(dateStr);
    const mins = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="flex h-screen w-full bg-background overflow-hidden">
      {/* Sidebar */}
      {(() => {
        const sidebarContent = (
          <>
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
          <StageFlowLogo className="w-9 h-9" />
          {!effectiveCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-[15px] tracking-tight text-white/95">StageFlow</h1>
              <p className="text-[11px] text-indigo-300">Theater Project Tracker</p>
            </div>
          )}
        </div>

        {/* Focus Mode Button */}
        <div className="px-3 pt-4 pb-2 space-y-1.5">
          <NavLink
            to="/new-deliverable"
            className={({ isActive }) => cn(
              'flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all',
              'bg-gradient-to-r from-indigo-500/20 to-violet-500/20 border border-indigo-400/30',
              'hover:from-indigo-500/30 hover:to-violet-500/30',
              isActive && 'from-indigo-500/40 to-violet-500/40 border-indigo-400/50',
              effectiveCollapsed && 'justify-center px-0'
            )}
          >
            <Plus className="w-4 h-4 text-indigo-300 shrink-0" />
            {!effectiveCollapsed && (
              <span className="text-[13px] text-indigo-200">New Deliverable</span>
            )}
          </NavLink>
          <NavLink
            to="/focus"
            className={({ isActive }) => cn(
              'flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all',
              'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30',
              'hover:from-amber-500/30 hover:to-orange-500/30',
              isActive && 'from-amber-500/40 to-orange-500/40 border-amber-400/50',
              effectiveCollapsed && 'justify-center px-0'
            )}
          >
            <Target className="w-4 h-4 text-amber-400 shrink-0" />
            {!effectiveCollapsed && (
              <span className="text-[13px] text-amber-200">Focus Mode</span>
            )}
          </NavLink>
          {/* Community section — collapsible group */}
          {effectiveCollapsed ? (
            /* Collapsed: show single community icon with badge */
            <NavLink
              to="/inbox"
              className={({ isActive }) => cn(
                'relative flex items-center justify-center px-0 py-2 rounded-lg transition-all',
                'bg-pink-500/10 border border-pink-400/20',
                'hover:bg-pink-500/20',
                (isActive || isCommunityActive) && 'bg-pink-500/25 border-pink-400/40',
              )}
              title="Community"
            >
              <Globe className="w-4 h-4 text-pink-400 shrink-0" />
              {displayCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none ring-2 ring-[#312e81]">
                  {displayCount > 99 ? '99+' : displayCount}
                </span>
              )}
            </NavLink>
          ) : (
            <div className="rounded-lg border border-pink-400/20 bg-pink-500/10 overflow-hidden">
              {/* Section header */}
              <button
                onClick={() => setCommunityOpen(!communityOpen)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all',
                  'hover:bg-pink-500/15',
                  isCommunityActive && 'bg-pink-500/15',
                )}
              >
                <Globe className="w-4 h-4 text-pink-400 shrink-0" />
                <span className="text-[13px] text-pink-200 flex-1 text-left">Community</span>
                {displayCount > 0 && (
                  <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none">
                    {displayCount > 99 ? '99+' : displayCount}
                  </span>
                )}
                <ChevronDown className={cn(
                  'w-3.5 h-3.5 text-pink-300/60 transition-transform duration-200',
                  !communityOpen && '-rotate-90'
                )} />
              </button>
              {/* Nested items */}
              {communityOpen && (
                <div className="pb-1 px-1 space-y-0.5">
                  <a
                    href="/board"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] text-pink-200/80 hover:bg-pink-500/15 hover:text-pink-100 transition-colors"
                  >
                    Board
                    <ExternalLink className="w-3 h-3 ml-auto text-pink-300/40" />
                  </a>
                  <NavLink
                    to="/inbox"
                    className={({ isActive }) => cn(
                      'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                      isActive
                        ? 'bg-pink-500/20 text-pink-100'
                        : 'text-pink-200/80 hover:bg-pink-500/15 hover:text-pink-100'
                    )}
                  >
                    Inbox
                    {displayCount > 0 && (
                      <span className="ml-auto flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none">
                        {displayCount > 99 ? '99+' : displayCount}
                      </span>
                    )}
                  </NavLink>
                  <NavLink
                    to="/community/form-builder"
                    className={({ isActive }) => cn(
                      'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                      isActive
                        ? 'bg-pink-500/20 text-pink-100'
                        : 'text-pink-200/80 hover:bg-pink-500/15 hover:text-pink-100'
                    )}
                  >
                    Form Builder
                  </NavLink>
                  <NavLink
                    to="/community/moderation"
                    className={({ isActive }) => cn(
                      'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                      isActive
                        ? 'bg-pink-500/20 text-pink-100'
                        : 'text-pink-200/80 hover:bg-pink-500/15 hover:text-pink-100'
                    )}
                  >
                    Moderation
                  </NavLink>
                  <NavLink
                    to="/community/polls"
                    className={({ isActive }) => cn(
                      'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                      isActive
                        ? 'bg-pink-500/20 text-pink-100'
                        : 'text-pink-200/80 hover:bg-pink-500/15 hover:text-pink-100'
                    )}
                  >
                    Polls
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => cn(
                'relative flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors group',
                'hover:bg-white/10',
                isActive ? 'text-white' : 'text-indigo-200',
                effectiveCollapsed && 'justify-center px-0'
              )}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-pill"
                      className="absolute inset-0 rounded-lg bg-white/15"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                  <item.icon className="relative z-10 w-[18px] h-[18px] shrink-0" />
                  {!effectiveCollapsed && (
                    <span className="relative z-10 text-[13px]">{item.label}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
          {/* Super Admin — admin only */}
          {isAdmin && (
            <NavLink
              to="/super-admin"
              className={({ isActive }) => cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all group',
                'hover:bg-white/10',
                isActive ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30' : 'text-indigo-200',
                effectiveCollapsed && 'justify-center px-0'
              )}
            >
              <Shield className="w-[18px] h-[18px] shrink-0" />
              {!effectiveCollapsed && (
                <span className="text-[13px]">Super Admin</span>
              )}
            </NavLink>
          )}
        </nav>

        {/* Settings Section */}
        <div className="px-3 pb-2">
          {effectiveCollapsed ? (
            /* Collapsed: show single settings icon */
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={cn(
                'relative flex items-center justify-center w-full py-2 rounded-lg transition-all',
                'bg-gray-500/10 border border-gray-400/20',
                'hover:bg-gray-500/20',
                isSettingsActive && 'bg-gray-500/25 border-gray-400/40',
              )}
              title="Settings"
            >
              <Settings className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
          ) : (
            <div className="rounded-lg border border-gray-400/20 bg-gray-500/10 overflow-hidden">
              {/* Section header */}
              <button
                onClick={() => setSettingsOpen(!settingsOpen)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all',
                  'hover:bg-gray-500/15',
                  isSettingsActive && 'bg-gray-500/15',
                )}
              >
                <Settings className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-[13px] text-gray-200 flex-1 text-left">Settings</span>
                <ChevronDown className={cn(
                  'w-3.5 h-3.5 text-gray-300/60 transition-transform duration-200',
                  !settingsOpen && '-rotate-90'
                )} />
              </button>
              {/* Nested items */}
              {settingsOpen && (
                <div className="pb-1 px-1 space-y-0.5">
                  {settingsItems.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => cn(
                        'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                        isActive
                          ? 'bg-gray-500/20 text-gray-100'
                          : 'text-gray-200/80 hover:bg-gray-500/15 hover:text-gray-100'
                      )}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                  {/* Form Options — admin only */}
                  {isAdmin && (
                    <NavLink
                      to="/settings/form-options"
                      className={({ isActive }) => cn(
                        'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                        isActive
                          ? 'bg-gray-500/20 text-gray-100'
                          : 'text-gray-200/80 hover:bg-gray-500/15 hover:text-gray-100'
                      )}
                    >
                      Form Options
                    </NavLink>
                  )}
                  {/* Marketing Site CMS — admin only */}
                  {isAdmin && (
                    <NavLink
                      to="/settings/marketing"
                      className={({ isActive }) => cn(
                        'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] transition-colors',
                        isActive
                          ? 'bg-gray-500/20 text-gray-100'
                          : 'text-gray-200/80 hover:bg-gray-500/15 hover:text-gray-100'
                      )}
                    >
                      Marketing Site
                    </NavLink>
                  )}
                  {/* TV Mode — opens in new tab */}
                  <a
                    href="/tv"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] text-gray-200/80 hover:bg-gray-500/15 hover:text-gray-100 transition-colors"
                  >
                    TV Mode
                    <ExternalLink className="w-3 h-3 ml-auto text-gray-300/40" />
                  </a>
                  {/* Check-in Kiosk — opens in new tab for the tablet at the door */}
                  <a
                    href="/checkin"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] text-gray-200/80 hover:bg-gray-500/15 hover:text-gray-100 transition-colors"
                  >
                    Check-in Kiosk
                    <ExternalLink className="w-3 h-3 ml-auto text-gray-300/40" />
                  </a>
                  {/* Mobile Focus Remote — open on phone/iPad */}
                  <a
                    href="/focus/mobile"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-[12px] text-gray-200/80 hover:bg-gray-500/15 hover:text-gray-100 transition-colors"
                  >
                    <Smartphone className="w-3 h-3 text-amber-400/60" />
                    Mobile Focus
                    <ExternalLink className="w-3 h-3 ml-auto text-gray-300/40" />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User / Logout */}
        {currentUser && (
          <div className="px-3 py-2 border-t border-white/10">
            {effectiveCollapsed ? (
              <div className="flex flex-col items-center gap-1.5">
                {!isMobile && (
                  <button
                    ref={notifButtonRef}
                    onClick={() => setNotifOpen(!notifOpen)}
                    className={cn(
                      'relative w-full flex items-center justify-center py-1.5 rounded-md transition-colors group',
                      notifOpen ? 'bg-white/10' : 'hover:bg-white/5'
                    )}
                    title="Mentions & Notifications"
                  >
                    <Bell className={cn(
                      'w-4 h-4 transition-colors',
                      notifOpen ? 'text-indigo-200' : 'text-indigo-300/60 group-hover:text-indigo-200'
                    )} />
                    {unreadNotifCount > 0 && (
                      <span className="absolute top-0 right-1.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-bold leading-none ring-2 ring-[#312e81]">
                        {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                      </span>
                    )}
                  </button>
                )}
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center py-1.5 rounded-md hover:bg-white/5 transition-colors group"
                  title="Log out"
                >
                  <LogOut className="w-4 h-4 text-indigo-300/60 group-hover:text-rose-400" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                  style={{ backgroundColor: currentUser.avatarColor || '#6366f1' }}
                >
                  {currentUser.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <span className="text-[12px] text-gray-200 truncate flex-1">{currentUser.name}</span>
                {!isMobile && (
                  <button
                    ref={notifButtonRef}
                    onClick={() => setNotifOpen(!notifOpen)}
                    className={cn(
                      'relative p-1 rounded-md transition-colors group',
                      notifOpen ? 'bg-white/10' : 'hover:bg-white/10'
                    )}
                    title="Mentions & Notifications"
                  >
                    <Bell className={cn(
                      'w-3.5 h-3.5 transition-colors',
                      notifOpen ? 'text-indigo-200' : 'text-indigo-300/60 group-hover:text-indigo-200'
                    )} />
                    {unreadNotifCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-bold leading-none ring-2 ring-[#1e1b4b]">
                        {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                      </span>
                    )}
                  </button>
                )}
                <button
                  onClick={logout}
                  className="p-1 rounded-md hover:bg-white/10 transition-colors group"
                  title="Log out"
                >
                  <LogOut className="w-3.5 h-3.5 text-indigo-300/60 group-hover:text-rose-400" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Keyboard / Help Hints */}
        {!effectiveCollapsed && !isMobile && (
          <div className="grid grid-cols-2 gap-1 border-t border-white/10 px-3 py-2">
            <button
              onClick={() => {
                // Trigger the keyboard shortcuts overlay by dispatching a "?" keypress
                const event = new KeyboardEvent('keydown', { key: '?' });
                window.dispatchEvent(event);
              }}
              className="group flex items-center justify-center gap-1.5 rounded-md py-1.5 transition-colors hover:bg-white/5"
            >
              <Keyboard className="h-3 w-3 text-indigo-300/60 group-hover:text-indigo-300" />
              <span className="text-[10px] text-indigo-300/60 group-hover:text-indigo-300">
                <kbd className="mx-0.5 rounded border border-white/10 bg-white/10 px-1 py-0.5 text-[9px]">?</kbd>
                shortcuts
              </span>
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('help:open-palette'))}
              className="group flex items-center justify-center gap-1.5 rounded-md py-1.5 transition-colors hover:bg-white/5"
              title="Search help — ⌘K"
            >
              <Search className="h-3 w-3 text-indigo-300/60 group-hover:text-indigo-300" />
              <span className="text-[10px] text-indigo-300/60 group-hover:text-indigo-300">
                <kbd className="mx-0.5 rounded border border-white/10 bg-white/10 px-1 py-0.5 text-[9px]">⌘K</kbd>
                help
              </span>
            </button>
          </div>
        )}

        {/* Collapse Toggle — desktop only */}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center justify-center py-3 border-t border-white/10 hover:bg-white/5 transition-colors"
          >
            {effectiveCollapsed ? (
              <ChevronRight className="w-4 h-4 text-indigo-300" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-indigo-300" />
            )}
          </button>
        )}
          </>
        );

        return isMobile ? (
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="w-[280px] p-0 bg-gradient-to-b from-[#1e1b4b] to-[#312e81] text-white border-r border-border [&>button]:text-white">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex flex-col h-full overflow-y-auto">
                {sidebarContent}
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <aside
            className={cn(
              'flex flex-col border-r border-border bg-gradient-to-b from-[#1e1b4b] to-[#312e81] text-white transition-all duration-300',
              effectiveCollapsed ? 'w-[68px]' : 'w-[240px]'
            )}
          >
            {sidebarContent}
          </aside>
        );
      })()}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto relative pt-16 md:pt-0">
        {/* Mobile hamburger menu */}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(true)}
            className="fixed top-4 left-4 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-md"
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* Mobile Notification Bell — fixed next to hamburger */}
        {currentUser && isMobile && (
          <div className="fixed top-4 left-16 z-50">
            <button
              ref={notifButtonRef}
              onClick={() => setNotifOpen(!notifOpen)}
              className={cn(
                'relative flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 shadow-md',
                notifOpen
                  ? 'bg-indigo-600 text-white shadow-indigo-200'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 hover:shadow-lg hover:-translate-y-0.5',
              )}
              title="Mentions & Notifications"
            >
              <Bell
                className={cn(
                  'w-[18px] h-[18px]',
                  unreadNotifCount > 0 && !notifOpen && 'animate-breathe',
                )}
              />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-none ring-2 ring-white">
                  {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Notification Dropdown — anchored to sidebar (desktop) or top bar (mobile) */}
        {currentUser && notifOpen && (
          <div
            ref={notifDropdownRef}
            className={cn(
              'fixed z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[480px] rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden',
              isMobile
                ? 'top-16 left-4'
                : effectiveCollapsed
                  ? 'bottom-4 left-[80px]'
                  : 'bottom-4 left-[252px]'
            )}
          >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <AtSign className="w-4 h-4 text-indigo-500" />
                    <span className="text-[13px] font-semibold text-gray-800">Mentions</span>
                    {unreadNotifCount > 0 && (
                      <span className="text-[11px] font-bold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded-full">
                        {unreadNotifCount} new
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {unreadNotifCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-gray-500 hover:bg-gray-100 transition-colors"
                        title="Mark all as read"
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        Read all
                      </button>
                    )}
                    <button
                      onClick={() => setNotifOpen(false)}
                      className="p-1 rounded-md text-gray-400 hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Notification List */}
                <div className="overflow-y-auto max-h-[400px]">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <AtSign className="w-6 h-6 text-gray-300" />
                      </div>
                      <p className="text-[13px] text-gray-400 font-medium">No mentions yet</p>
                      <p className="text-[11px] text-gray-300 mt-1">When someone @mentions you in a message, it will appear here</p>
                    </div>
                  ) : (
                    notifications.slice(0, 30).map(notif => (
                      <button
                        key={notif.id}
                        onClick={() => handleNotifClick(notif)}
                        className={cn(
                          'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50',
                          notif.read
                            ? 'bg-white hover:bg-gray-50'
                            : 'bg-indigo-50/50 hover:bg-indigo-50'
                        )}
                      >
                        {/* Sender avatar */}
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5"
                          style={{ backgroundColor: notif.senderColor || '#6366f1' }}
                        >
                          {notif.senderName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-[12px] font-semibold text-gray-800 truncate">{notif.senderName}</span>
                            <span className="text-[10px] text-gray-400">{relativeTime(notif.createdAt)}</span>
                            {!notif.read && (
                              <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500 mb-1">
                            {notif.type === 'handoff' ? (
                              <>handed off <span className="font-medium text-gray-700">{notif.deliverableTitle}</span> to you</>
                            ) : notif.type === 'agenda' ? (
                              <>
                                wants to talk this through with you
                                {notif.deliverableTitle && <> on <span className="font-medium text-gray-700">{notif.deliverableTitle}</span></>}
                              </>
                            ) : (
                              <>mentioned you on <span className="font-medium text-gray-700">{notif.deliverableTitle}</span></>
                            )}
                          </p>
                          <p className="text-[11px] text-gray-400 truncate leading-relaxed">
                            "{notif.messagePreview}"
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
          </div>
        )}

        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>

      {/* Global Voice Command Button — bottom-right */}
      {currentUser && (
        <div className="fixed bottom-6 right-6 z-40">
          <VoiceCommandButton />
        </div>
      )}

      {/* Global Feedback Button — stacked above the voice button; positions
          itself (fixed) and renders nothing when signed out */}
      {currentUser && <FeedbackButton />}

      {/* Global Keyboard Shortcuts Overlay */}
      <KeyboardShortcutsOverlay />

      {/* Global ⌘K help / command palette */}
      <HelpSearchDialog />

      {/* Daily reset — verse + personalized AI briefing at the reset hour */}
      <DailyResetOverlay />
    </div>
  );
}